function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { spacing, icons, themeLight } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import { Close } from '@styled-icons/material/Close';
import { Check } from '@styled-icons/material/Check';
import { Text, TextVariant } from '@naf/text';
import { propValidationConfig } from './propValidationConfig';
const renderIcon = icon => {
  switch (icon) {
    case 'close':
      return /*#__PURE__*/React.createElement(Close, {
        size: 16
      });
    case 'checked':
      return /*#__PURE__*/React.createElement(Check, {
        size: 16
      });
    default:
      return icon;
  }
};
export const Chips = ({
  size = 'standard',
  variant = 'outline',
  icon,
  text,
  disabled,
  ariaLabel,
  onClick,
  className,
  ...rest
}) => {
  const [mouseDown, setMouseDown] = useState(false);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  useEffect(() => {
    window.addEventListener('mousedown', removeOutline);
    return () => window.removeEventListener('mousedown', removeOutline);
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', addOutline);
    return () => window.removeEventListener('keydown', addOutline);
  }, []);
  const chipVariant = size => {
    switch (size) {
      case 'small':
        return TextVariant.Small;
      case 'standard':
        return TextVariant.BodyText;
      default:
        return TextVariant.BodyText;
    }
  };
  return /*#__PURE__*/React.createElement(ClickableArea, {
    className: className,
    variant: variant,
    disabled: disabled,
    onClick: disabled ? undefined : onClick
  }, /*#__PURE__*/React.createElement(Chip, _extends({
    size: size,
    variant: variant,
    icon: icon,
    "aria-label": ariaLabel,
    id: mouseDown ? 'mousedown' : '',
    disabled: disabled
  }, rest), /*#__PURE__*/React.createElement(StyledText, {
    variant: chipVariant(size)
  }, text), icon && renderIcon(icon)));
};
export default Chips;
const Chip = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "chips__Chip",
  componentId: "sc-js64iw-0"
})(["display:inline-flex;justify-content:center;align-items:center;border-radius:50px;margin:", ";border:1px solid transparent;box-sizing:border-box;position:relative;color:", ";", " ", " ", " ", " ", " ", " ", " appearance:none;&:hover{cursor:pointer;}", " ", " ", " ", " ", " ", " ", " &#mousedown{outline:0;}"], spacing.space4, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  variant
}) => variant === 'outline' && css(["background:'transparent';border-color:", ";"], ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy), ({
  variant
}) => variant === 'signature' && css(["background:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active), ({
  variant
}) => variant === 'moss' && css(["background:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.cta.secondary : themeLight.componentColors.cta.secondary), ({
  variant
}) => variant === 'signature40' && css(["background:", ";"], nafColor.signature.yellow40), ({
  variant
}) => variant === 'outline' && css(["background:", ";border-color:", ";"], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy), ({
  size
}) => size === 'small' && css(["padding:0 ", ";height:", ";"], spacing.space8, spacing.space24), ({
  size
}) => size === 'standard' && css(["padding:0 ", ";height:", ";"], spacing.space16, spacing.space32), ({
  icon
}) => icon === 'close' && css(["svg{order:1;padding-left:", ";font-size:", ";}"], spacing.space8, icons.s), ({
  icon
}) => icon === 'checked' && css(["svg{order:-1;padding-right:", ";font-size:", ";}"], spacing.space8, icons.s), ({
  icon
}) => icon && icon !== 'checked' && icon !== 'close' && css(["svg{order:1;padding-left:", ";font-size:", ";}"], spacing.space8, icons.s), ({
  icon,
  size
}) => icon === 'close' && size === 'small' && css(["svg{padding-left:", ";font-size:", ";}"], spacing.space4, spacing.space12), ({
  icon,
  size
}) => icon === 'checked' && size === 'small' && css(["svg{padding-right:", ";font-size:", ";}"], spacing.space4, spacing.space12), ({
  icon,
  size
}) => icon && size === 'small' && css(["svg{padding-left:", ";font-size:", ";}"], spacing.space4, spacing.space12), ({
  disabled
}) => disabled && css(["background-color:", ";color:", ";border:1px solid transparent;cursor:not-allowed;&:hover{cursor:not-allowed;border:1px solid transparent;}"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.disabled : themeLight.componentColors.interactiveElement.disabled, nafColor.neutral.neutral5));
const StyledText = styled(Text).withConfig({
  displayName: "chips__StyledText",
  componentId: "sc-js64iw-1"
})(["margin:0;"]);
const ClickableArea = styled.button.withConfig(propValidationConfig).withConfig({
  displayName: "chips__ClickableArea",
  componentId: "sc-js64iw-2"
})(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;appearance:none;touch-action:manipulation;min-height:", ";min-width:", ";height:fit-content;width:fit-content;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;", " &:focus,&:focus-visible{outline:0 !important;", "{outline:1px solid ", ";}}&:hover{", "{", " ", " ", " ", "}}"], spacing.space40, spacing.space40, ({
  disabled
}) => disabled && css(["cursor:initial;"]), Chip, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, Chip, ({
  variant
}) => variant === 'outline' && css(["border-color:", ";"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default), ({
  variant
}) => variant === 'signature' && css(["border-color:", ";"], ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy), ({
  variant
}) => variant === 'signature40' && css(["border-color:", ";"], ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy), ({
  variant
}) => variant === 'moss' && css(["border-color:", ";"], ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy));
//# sourceMappingURL=chips.js.map